import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ClippingProject, type PageDetail, type Region } from '../../lib/api'
import { Chevron } from '../Page'
import { formatCitation, type CitationStyle } from './citation'
import {
  bboxToIiif,
  columnsLabel,
  measureLabel,
  permalinkPath,
  toPt,
  type PageGeometry,
  type RegionItem,
} from './model'

/**
 * A new clipping.
 *
 * The citation is composed from the region's own record (page, columns, date)
 * and frozen on save, so later enrichment cannot change what a footnote
 * already claims. The clipping's bbox is the citation's real subject: a
 * clipping that cannot be resolved back to the pixels it was cut from is a
 * screenshot, not a citation.
 */

const STYLES: CitationStyle[] = ['mla', 'chicago', 'apa', 'bibtex']
const STYLE_LABELS: Record<string, string> = {
  mla: 'MLA 9',
  chicago: 'CHICAGO',
  apa: 'APA',
  bibtex: 'BIBTEX',
}

export function ClipPanel({
  page,
  geometry,
  bbox,
  region,
  productName,
  onCancel,
  onSaved,
}: {
  page: PageDetail
  geometry: PageGeometry
  bbox: Region
  region: RegionItem | null
  productName: string
  onCancel: () => void
  onSaved: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(region?.title?.slice(0, 90) ?? '')
  const [projectId, setProjectId] = useState('')
  const [style, setStyle] = useState<CitationStyle>('mla')
  const input = useRef<HTMLInputElement>(null)

  const projects = useQuery({
    queryKey: ['clipping-projects'],
    queryFn: () => api.get<ClippingProject[]>('/clipping-projects'),
    retry: false,
  })

  useEffect(() => {
    input.current?.focus()
  }, [])

  const citation = useMemo(
    () =>
      formatCitation(style, {
        headline: title.trim() || region?.title || 'Untitled clipping',
        publication: geometry.publication,
        place: null,
        date: geometry.pubDate,
        pageLabel: geometry.label,
        columns: columnsLabel(bbox, geometry),
        permalink: `${window.location.origin}${permalinkPath(geometry, bbox)}`,
        productName,
        accessed: new Date().toISOString().slice(0, 10),
      }),
    [style, title, region, geometry, bbox, productName],
  )

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/clippings', {
        page_id: page.id,
        bbox,
        note: title.trim() || null,
        project_id: projectId || null,
        article_id: region?.articleId ?? null,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['clippings'] })
      onSaved(created.id)
    },
  })

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
      if (event.key === 'Enter' && !save.isPending) {
        event.preventDefault()
        save.mutate()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, save])

  const width = bbox[2] - bbox[0]
  const height = bbox[3] - bbox[1]

  return (
    <div className="inspector flex min-h-0 w-full flex-col gap-2.5 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <span className="badge" style={{ background: 'var(--jl-accent)' }}>
          CLIP
        </span>
        <div className="chrome">New clipping</div>
      </div>

      <div className="rounded-[--radius-md] bg-surface px-2.5 py-2 font-mono text-[11.5px] leading-[1.6]">
        <div>
          {`${Math.round(width)} × ${Math.round(height)} px · ${toPt(width, geometry.ppi).toFixed(
            1,
          )} × ${toPt(height, geometry.ppi).toFixed(1)} pt`}
        </div>
        <div className="text-ink2">
          {`${bboxToIiif(bbox)} · cols ${columnsLabel(bbox, geometry)} · ${measureLabel(
            bbox,
            geometry,
          )}`}
        </div>
      </div>

      <label className="block">
        <span className="sr-only">Title or note for this clipping</span>
        <input
          ref={input}
          className="input py-1.5 text-[13.5px]"
          value={title}
          placeholder="What is this clipping for?"
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <div className="flex items-center gap-2 text-[13.5px]">
        <span className="text-ink2">Project</span>
        <div className="relative flex-1">
          <select
            className="input appearance-none py-1.5 pr-7 text-[13.5px]"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            aria-label="Clipping project"
          >
            <option value="">No project</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink2">
            <Chevron direction="down" size={11} />
          </span>
        </div>
      </div>

      <div className="rounded-[--radius-md] bg-surface px-3 py-2.5">
        <div className="mb-1.5 font-mono text-[9.5px] tracking-[0.12em] text-ink2">
          CITATION · FROZEN AT CREATION
        </div>
        <div className="whitespace-pre-wrap text-[13.5px] leading-normal">{citation}</div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-ink2">
        {STYLES.map((option, index) => (
          <span key={option} className="flex items-center gap-1.5">
            {index > 0 && <span className="text-rule2">·</span>}
            <button
              type="button"
              aria-pressed={option === style}
              onClick={() => setStyle(option)}
              className={option === style ? 'text-ink' : 'text-accent'}
            >
              {STYLE_LABELS[option]}
            </button>
          </span>
        ))}
      </div>

      {save.isError && (
        <p className="doubt-block rounded-r-[--radius-md] text-[13.5px]">
          The clipping could not be saved: {(save.error as Error).message}
        </p>
      )}

      <div className="mt-auto flex items-center gap-2 pt-2">
        <button
          type="button"
          className="btn-primary px-3.5 py-1.5"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? 'Saving' : 'Save'}
        </button>
        <button type="button" className="btn-secondary px-3.5 py-1.5" onClick={onCancel}>
          Cancel
        </button>
        <span className="ml-auto self-center font-mono text-[10px] text-ink3">ENTER / ESC</span>
      </div>
    </div>
  )
}
